import * as React from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import ErrorMsg from '../common/ErrorMsg';
import Fields from './Fields';
import errorResponses from '../../errorResponses';
import useGlynt from '../hooks/useGlynt';
import usePaginatedFields from '../hooks/usePaginatedFields';
import { AppContext } from '../../App';
import { IField } from '../../types/my-types';
import { useQuery } from '@tanstack/react-query';
import { useSetTitle } from '../hooks/useSetTitle';

interface FieldsDataProps {
  trainingSetId: string;
}

const FieldsData: React.FunctionComponent<FieldsDataProps> = ({ trainingSetId }) => {
  const { dataPoolId } = React.useContext(AppContext);
  const glynt = useGlynt();

  const tsQuery = useQuery({
    queryKey: ['trainingSet', trainingSetId],
    queryFn: async () => await (await glynt).trainingSet.retrieve(trainingSetId),
    enabled: !!dataPoolId,

    // 5 min
    staleTime: 5 * 60 * 1000,

    // 10 min
    gcTime: 10 * 60 * 1000,

    refetchOnWindowFocus: false
  });

  useSetTitle(tsQuery.data?.label ? `Fields - ${tsQuery.data?.label}` : '...');

  const dtQuery = useQuery({
    queryKey: ['dataTypes'],
    queryFn: async () => await (await glynt).dataTypes.retrieve(),
    enabled: !!dataPoolId,

    // 5 min
    staleTime: 5 * 60 * 1000,

    // 10 min
    gcTime: 10 * 60 * 1000,

    refetchOnWindowFocus: false
  });

  const paginatedFieldsData = usePaginatedFields(trainingSetId);

  if (tsQuery.isPending || paginatedFieldsData.loading) {
    return <LinearProgress />;
  }
  if (tsQuery.isError || paginatedFieldsData.error) {
    return <ErrorMsg error={errorResponses.invalidTrainingListId} />;
  }

  const dataTypesResponse = dtQuery?.data;
  const fields: IField[] = paginatedFieldsData.data?.results || [];
  const trainingSet = tsQuery.data;

  return (
    <Fields
      dataTypesResponse={dataTypesResponse || []}
      fields={fields}
      page={paginatedFieldsData.page}
      total={paginatedFieldsData.total}
      trainingSetDataTypeConfig={trainingSet?.data_type_config}
      trainingSetId={trainingSetId}
      trainingSetLabel={trainingSet?.label || ''}
      trainingSetDataTypeIsGlobal={trainingSet?.using_global_data_type_config}
      previousPage={paginatedFieldsData.previousPage}
      nextPage={paginatedFieldsData.nextPage}
    />
  );
};

export default FieldsData;
